import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpRequestDecorator } from 'app/core/triggerbee/http.request.decorator';
import { communityResource } from './community-gateway';
import { communityParams } from './models/community-paging';
import {
  CommunityNotification,
  NotificationFeed,
  NotificationUnreadCount,
  NotificationsSeenResult,
  PushSubscriptionRequest,
  VapidPublicKey,
} from './models/community-notifications.models';

/**
 * The bell and the browser's push registration.
 *
 * Every route here is scoped to the signed-in user by the Gateway, which stamps the recipient from the
 * cookie and ignores a supplied one — so somebody else's notification is a 404 rather than a 403, and a
 * recipient never learns that an event exists.
 */
@Injectable()
export class CommunityNotificationsService {
  private readonly http = inject(HttpRequestDecorator);

  public getFeed(skip: number, take: number | null): Observable<NotificationFeed> {
    return this.http.get(this.resource('notifications/feed'), communityParams({ skip, take }));
  }

  public getUnreadCount(): Observable<NotificationUnreadCount> {
    return this.http.get(this.resource('notifications/unread-count'));
  }

  /**
   * Null when the environment has no key configured: the route answers 204 with an empty body, which the
   * HTTP layer surfaces as null. The only absent answer on the surface that is deliberately not a 404, and
   * the branch a caller must take before offering push at all.
   */
  public getVapidPublicKey(): Observable<VapidPublicKey | null> {
    return this.http.get(this.resource('notifications/vapid-public-key'));
  }

  /** A read, and only a read: seeing an event does not mark it seen. */
  public getById(id: string): Observable<CommunityNotification> {
    return this.http.get(this.resource(`notifications/${id}`));
  }

  public markSeen(id: string): Observable<void> {
    return this.http.post(this.resource(`notifications/${id}/seen`), {});
  }

  public markAllSeen(): Observable<NotificationsSeenResult> {
    return this.http.post(this.resource('notifications/seen-all'), {});
  }

  /**
   * The subscription travels flattened. PushSubscription.toJSON() nests p256dh and auth under `keys`, and
   * posting that shape answers 204 while storing nothing, so the push simply never arrives.
   */
  public subscribe(subscription: PushSubscriptionRequest): Observable<void> {
    return this.http.post(this.resource('notifications/subscribe'), subscription);
  }

  public unsubscribe(endpoint: string): Observable<void> {
    return this.http.post(this.resource('notifications/unsubscribe'), { endpoint });
  }

  private resource(path: string): string {
    return communityResource(this.http, path);
  }
}
